import { type ReactNode, type CSSProperties, forwardRef, type ButtonHTMLAttributes, type HTMLAttributes, type LiHTMLAttributes } from 'react';
import { clsx } from 'clsx';
import { wrapTextChild } from '../wrapTextChild';
import {
  listRoot, listRootDense, listItem, listItemPadding, listItemDivider,
  listItemButton, listItemButtonDense,
  listItemIcon, listItemText, listItemPrimary, listItemSecondary,
  listSubheader, listItemAvatar,
} from './list.css';

// ============================================================
// List
// ============================================================

export interface ListProps extends HTMLAttributes<HTMLUListElement> {
  dense?: boolean;
  disablePadding?: boolean;
}

export const List = forwardRef<HTMLUListElement, ListProps>(
  ({ dense = false, disablePadding: _disablePadding = false, className, children, ...props }, ref) => (
    <ul
      ref={ref}
      className={clsx(listRoot, dense && listRootDense, className)}
      {...props}
    >
      {children}
    </ul>
  ),
);
List.displayName = 'List';

// ============================================================
// ListItem
// ============================================================

export interface ListItemProps extends LiHTMLAttributes<HTMLLIElement> {
  disablePadding?: boolean;
  divider?: boolean;
  secondaryAction?: ReactNode;
}

export const ListItem = forwardRef<HTMLLIElement, ListItemProps>(
  ({ disablePadding = false, divider = false, secondaryAction, className, children, ...props }, ref) => (
    <li
      ref={ref}
      className={clsx(
        listItem,
        !disablePadding && listItemPadding,
        divider && listItemDivider,
        className,
      )}
      {...props}
    >
      {wrapTextChild(children)}
      {secondaryAction && (
        <div style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }}>
          {secondaryAction}
        </div>
      )}
    </li>
  ),
);
ListItem.displayName = 'ListItem';

// ============================================================
// ListItemButton
// ============================================================

export interface ListItemButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  dense?: boolean;
  selected?: boolean;
}

export const ListItemButton = forwardRef<HTMLButtonElement, ListItemButtonProps>(
  ({ dense = false, selected = false, className, children, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      data-selected={selected || undefined}
      className={clsx(listItemButton, dense && listItemButtonDense, className)}
      {...props}
    >
      {children}
    </button>
  ),
);
ListItemButton.displayName = 'ListItemButton';

// ============================================================
// ListItemIcon
// ============================================================

export interface ListItemIconProps extends HTMLAttributes<HTMLDivElement> {
  style?: CSSProperties;
}

export const ListItemIcon = forwardRef<HTMLDivElement, ListItemIconProps>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={clsx(listItemIcon, className)} {...props}>
      {children}
    </div>
  ),
);
ListItemIcon.displayName = 'ListItemIcon';

// ============================================================
// ListItemText
// ============================================================

export interface ListItemTextProps extends HTMLAttributes<HTMLDivElement> {
  primary?: ReactNode;
  secondary?: ReactNode;
  primaryTypographyProps?: HTMLAttributes<HTMLSpanElement>;
  secondaryTypographyProps?: HTMLAttributes<HTMLSpanElement>;
}

export const ListItemText = forwardRef<HTMLDivElement, ListItemTextProps>(
  ({ primary, secondary, primaryTypographyProps, secondaryTypographyProps, className, children, ...props }, ref) => (
    <div ref={ref} className={clsx(listItemText, className)} {...props}>
      {primary !== undefined ? (
        <span className={listItemPrimary} {...primaryTypographyProps}>{primary}</span>
      ) : children}
      {secondary !== undefined && (
        <span className={listItemSecondary} {...secondaryTypographyProps}>{secondary}</span>
      )}
    </div>
  ),
);
ListItemText.displayName = 'ListItemText';

// ============================================================
// ListItemAvatar
// ============================================================

export type ListItemAvatarProps = HTMLAttributes<HTMLDivElement>;

export const ListItemAvatar = forwardRef<HTMLDivElement, ListItemAvatarProps>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={clsx(listItemAvatar, className)} {...props}>
      {children}
    </div>
  ),
);
ListItemAvatar.displayName = 'ListItemAvatar';

// ============================================================
// ListSubheader
// ============================================================

export type ListSubheaderProps = HTMLAttributes<HTMLDivElement>;

export const ListSubheader = forwardRef<HTMLDivElement, ListSubheaderProps>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={clsx(listSubheader, className)} {...props}>
      {children}
    </div>
  ),
);
ListSubheader.displayName = 'ListSubheader';
